import React from "react";
import * as m from "../../lib/model";
import { Card, Tile } from "../shared";
import { Trophy, ArrowDownToLine } from "lucide-react";

export const annualDS = (l) => (l.amount > 0 && l.rate > 0 ? m.amortMonthly(+l.amount, +l.rate, +l.amortYears || +l.termYears || 10) * 12 : 0);
const recourseScore = (r) => (r === "Non-recourse" ? 1 : r === "Limited recourse" ? 0.5 : 0);

// Composite score: cheaper rate, more proceeds, faster close, less recourse, lower fees.
export const rankLenders = (rows, targetDebt) => {
  const live = rows.filter((l) => l.stage !== "Passed" && l.amount > 0 && l.rate > 0);
  if (!live.length) return [];
  const mn = (k) => Math.min(...live.map((l) => +l[k] || 0)), mx = (k) => Math.max(...live.map((l) => +l[k] || 0));
  const norm = (v, lo, hi, invert = false) => (hi === lo ? 1 : invert ? (hi - v) / (hi - lo) : (v - lo) / (hi - lo));
  return live.map((l) => {
    const rate = norm(+l.rate, mn("rate"), mx("rate"), true);
    const amount = targetDebt > 0 ? Math.min(1, (+l.amount || 0) / targetDebt) : norm(+l.amount, mn("amount"), mx("amount"));
    const close = norm(+l.closingDays || 0, mn("closingDays"), mx("closingDays"), true);
    const fees = norm(+l.feesPct || 0, mn("feesPct"), mx("feesPct"), true);
    const score = 0.35 * rate + 0.25 * amount + 0.15 * close + 0.15 * recourseScore(l.recourse) + 0.10 * fees;
    return { ...l, score: Math.round(score * 100), ds: annualDS(l) };
  }).sort((a, b) => b.score - a.score);
};

const best = (rows, k, min = true) => {
  const vals = rows.map((r) => +r[k] || 0).filter((v) => v > 0);
  if (!vals.length) return null;
  return min ? Math.min(...vals) : Math.max(...vals);
};

export const DebtScoreboard = ({ rows, targetDebt, ebitda, cfads, tl, onOpenTimeline }) => {
  const live = rows.filter((l) => l.stage !== "Passed");
  const ts = live.filter((l) => ["Term Sheet", "Negotiating", "Committed", "Closed"].includes(l.stage));
  const committed = live.filter((l) => ["Committed", "Closed"].includes(l.stage)).reduce((s, l) => s + (+l.amount || 0), 0);
  const ranked = rankLenders(rows, targetDebt);
  const top = ranked[0];
  const bestRate = best(ts, "rate"), maxAmt = best(ts, "amount", false), fastest = best(ts, "closingDays");
  const dscrTop = top && cfads > 0 && top.ds > 0 ? cfads / top.ds : 0;
  return (
    <div className="cm-tiles" data-testid="debt-scoreboard">
      <Tile label="Debt needed" value={m.fmt$(targetDebt)} sub={`${ebitda > 0 ? m.fmtX(targetDebt / ebitda) : "—"} EBITDA · from Assumptions`} testid="dt-target" />
      <div onClick={onOpenTimeline} className="cm-tile-link" role="button" data-testid="dt-tile-close-wrap">
        <Tile label="Days to funding" value={tl ? (tl.daysToClose < 0 ? `${-tl.daysToClose}d past` : `${tl.daysToClose} days`) : "—"} sub={tl ? `fund ${new Date(tl.closeDate + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} · week ${Math.min(tl.weeksElapsed || 1, tl.weekCount)} of ${tl.weekCount} · ${tl.tasksDone}/${tl.tasksTotal} done` : "set a funding date in Closing Timeline"} tone={tl && tl.daysToClose < 0 ? "pass" : undefined} testid="dt-close-tile" />
      </div>
      <Tile label="Lenders in play" value={live.length} sub={`${ts.length} term sheet(s) received`} testid="dt-lenders" />
      <Tile label="Best rate" value={bestRate ? `${bestRate.toFixed(2)}%` : "—"} sub={bestRate ? ts.find((l) => +l.rate === bestRate)?.lender : "no term sheets yet"} testid="dt-rate" />
      <Tile label="Max proceeds" value={maxAmt ? m.fmt$(maxAmt) : "—"} sub={maxAmt ? `${ts.find((l) => +l.amount === maxAmt)?.lender} · ${targetDebt ? `${Math.round((maxAmt / targetDebt) * 100)}% of need` : ""}` : "—"} testid="dt-amount" />
      <Tile label="Fastest close" value={fastest ? `${fastest}d` : "—"} sub={fastest ? ts.find((l) => +l.closingDays === fastest)?.lender : "—"} testid="dt-close" />
      <Tile label="Committed" value={m.fmt$(committed)} sub={targetDebt ? `${Math.round((committed / targetDebt) * 100)}% of need` : ""} tone={committed >= targetDebt && targetDebt > 0 ? "go" : undefined} testid="dt-committed" />
      <Tile label="Front-runner" value={top ? top.lender : "—"} sub={top ? `score ${top.score} · ${top.rate}% · ${m.fmt$(top.amount)} · ${top.recourse}` : "add terms to rank"} tone={top ? "go" : undefined} testid="dt-top" />
      <Tile label="DSCR on front-runner" value={dscrTop ? m.fmtX(dscrTop) : "—"} sub={top ? `${m.fmt$(top.ds)} annual debt service` : "—"} tone={dscrTop ? m.dscrTone(dscrTop) : undefined} testid="dt-dscr" />
    </div>
  );
};

const COLS = [
  ["amount", "Amount", (v) => m.fmt$(v), false],
  ["lev", "Leverage", (v) => m.fmtX(v), false],
  ["rate", "Rate", (v, l) => `${(+v).toFixed(2)}%${l.rateType && !l.rateType.startsWith("Fixed") ? " fl." : ""}`, true],
  ["termYears", "Term", (v) => `${v} yr`, false],
  ["amortYears", "Amort.", (v) => `${v} yr`, false],
  ["ds", "Annual DS", (v) => m.fmt$(v), true],
  ["closingDays", "Close", (v) => `${v} d`, true],
  ["feesPct", "Fees", (v) => `${(+v).toFixed(2)}%`, true],
];

export const TermCompare = ({ rows, targetDebt, ebitda, cfads, onPick }) => {
  const ranked = rankLenders(rows, targetDebt).map((l) => ({ ...l, lev: ebitda > 0 ? (+l.amount || 0) / ebitda : 0 }));
  const bestOf = Object.fromEntries(COLS.map(([k, , , min]) => {
    const vals = ranked.map((r) => +r[k] || 0).filter((v) => v > 0);
    return [k, vals.length ? (min ? Math.min(...vals) : Math.max(...vals)) : null];
  }));
  return (
    <Card title={`Term sheet comparison — ${ranked.length} priced lender${ranked.length === 1 ? "" : "s"}, ranked`} testid="term-compare" className="table-scroll">
      {ranked.length === 0 && <div className="cm-empty-block">No priced proposals yet. Add a lender with an amount and rate to start the comparison.</div>}
      {ranked.length > 0 && (
        <table className="tbl tc-tbl">
          <thead><tr><th>#</th><th>Lender</th><th>Stage</th>{COLS.map(([k, h]) => <th key={k} className="num">{h}</th>)}<th className="num">DSCR</th><th /></tr></thead>
          <tbody>
            {ranked.map((l, i) => (
              <React.Fragment key={l.id}>
                <tr className={`tc-main ${i === 0 ? "hl" : ""}`} data-testid={`tc-row-${l.id}`}>
                  <td className="mono">{i === 0 ? <Trophy size={12} style={{ color: "var(--lime)" }} /> : i + 1}</td>
                  <td className="tc-lender"><b>{l.lender}</b><div className="tc-sub">{l.type}{l.contact ? ` · ${l.contact}` : ""}<br />score {l.score}</div></td>
                  <td><span className="tag">{l.stage}</span></td>
                  {COLS.map(([k, , f]) => <td key={k} className={`num ${bestOf[k] !== null && +l[k] === bestOf[k] && +l[k] > 0 ? "tc-best" : ""}`}>{+l[k] > 0 ? f(+l[k], l) : "—"}</td>)}
                  <td className={`num ${cfads > 0 && l.ds > 0 ? (m.dscrTone(cfads / l.ds) === "go" ? "pos" : m.dscrTone(cfads / l.ds) === "pass" ? "neg" : "") : ""}`}>{cfads > 0 && l.ds > 0 ? m.fmtX(cfads / l.ds) : "—"}</td>
                  <td><button className="btn btn-sm" onClick={() => onPick(l)} title="Push these terms into Assumptions" data-testid={`tc-pick-${l.id}`}><ArrowDownToLine size={12} />Use terms</button></td>
                </tr>
                <tr className={`tc-detail ${i === 0 ? "hl" : ""}`}>
                  <td colSpan={COLS.length + 5}>
                    <div className="tc-chips" data-testid={`tc-detail-${l.id}`}>
                      <span className={`tc-chip ${l.recourse === "Non-recourse" ? "good" : ""}`}>Recourse <b>{l.recourse || "—"}</b></span>
                      <span className="tc-chip">Personal guarantee <b>{l.personalGuarantee || "—"}</b></span>
                      <span className="tc-chip">Prepay penalty <b>{l.prepay || "—"}</b></span>
                      <span className="tc-chip">Deposit req. <b>{l.depositReq || "—"}</b></span>
                      {l.covenants && <span className="tc-chip tc-cov">Covenants <b>{l.covenants}</b></span>}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
      <div className="cm-empty tc-note">
        <span>Score = 35% rate · 25% proceeds vs need · 15% speed · 15% recourse · 10% fees.</span>
        <span>Green cells are the best value in each column.</span>
        <span>"Use terms" rewrites the deal's debt assumptions (amount, rate, term) so returns and DSCR reflect that lender.</span>
      </div>
    </Card>
  );
};
